/**
 * ModalErrorBoundary - Error boundary for the multi-disease modal
 *
 * Catches rendering errors and shows a fallback UI that allows users
 * to dismiss the modal and continue using the app.
 */

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  onDismiss: () => void;
}

interface State {
  hasError: boolean;
}

export class ModalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error for debugging (could send to error tracking service)
    console.error('MultiDiseaseExperimentModal error:', error, errorInfo);
  }

  handleDismiss = (): void => {
    this.props.onDismiss();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center gap-6 py-4 text-center">
          <div>
            <h2 className="text-2xl font-bold text-catch-dark-blue-90">
              Something went wrong
            </h2>
            <p className="mt-3 text-catch-slate">
              We encountered an issue loading this screen. Click below to continue.
            </p>
          </div>

          <button
            onClick={this.handleDismiss}
            className="flex w-full items-center justify-center rounded-full bg-catch-avocado-400 px-5 py-4 text-sm font-bold text-catch-dark-blue-90 transition-opacity hover:opacity-90"
          >
            Continue to Catch
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ModalErrorBoundary;
